import LegalSection, { LegalSection as LegalSectionType } from '@/components/ui/LegalSection';
import { createFileRoute } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import { useEffect } from 'react';

export const Route = createFileRoute('/_public/terms-of-service')({
  component: TermsPage,
})

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Service | Swim Homes';
  }, []);

  const sections: LegalSectionType[] = [
    {
      title: "",
      content: [
        "Welcome to Swim Homes (\"Swim\", \"we\", \"us\", or \"our\"). Swim Homes provides a platform that allows individuals in Ontario to list, buy, and sell homes without the need for a real estate agent or broker. Our service operates on a flat-fee basis, providing homeowners and buyers the tools they need to complete real estate transactions independently. These Terms and Conditions (\"Terms\") govern your use of our website, services, AI features, and any related products offered through our platform, accessible at https://www.swimhomes.ca.",
        "By using our services, you agree to be bound by these Terms. If you do not agree to these Terms, please refrain from using our website or any of our services."
      ]
    },
    {
      title: "1. Acceptance of Terms",
      content: [
        "By accessing or using the Swim Homes platform, you agree to comply with these Terms and any policies referenced within, including our Privacy Policy and AI Disclaimer. These Terms apply to all visitors, users, and others who access the website or use our services. The Privacy Policy and AI Disclaimer are incorporated into and form a part of these Terms.",
        "You acknowledge that these Terms form a binding agreement between you and Swim Homes, and you represent and warrant that you have the legal capacity to enter into these Terms. If you are accessing or using our services on behalf of a business entity, you confirm that you have the authority to bind the entity to these Terms."
      ]
    },
    {
      title: "2. Eligibility",
      content: [
        "You must be at least 18 years of age to access or use the Swim Homes platform. By accessing or using our services, you represent and warrant that you are at least 18 years old. Individuals under the age of 18 are strictly prohibited from using our website and services.",
      ]
    },
    {
      title: "3. Platform Purpose",
      content: [
        "Swim Homes provides a platform where users can list, buy, and sell homes directly without the involvement of real estate agents. The platform allows for the listing of properties for sale and for potential buyers to make inquiries, view listings, and negotiate with sellers. We offer flat-fee services that include tools to list properties, schedule showings, and manage certain transaction-related tasks, but we are not a licensed real estate brokerage or agent.",
        "Swim Homes may partner with third-party realtors solely for the purpose of syndicating listings to multiple listing services (MLS). This partnership does not establish any form of realtor-client or broker-client relationship between Swim Homes, you, or any third-party realtors. Any agreement or arrangement you enter into with third-party realtors will be separate from your use of our platform and should be carefully reviewed by you.",
      ]
    },
    {
      title: "4. No Realtor-Client or Broker-Client Relationship",
      content: [
        "You acknowledge that Swim Homes is not a licensed realtor, broker, or legal advisor. We provide tools and resources to help you sell or buy property, but we do not represent you in any legal or real estate capacity. We do not enter into any contractual agreements on your behalf nor do we facilitate the transfer of legal ownership in real estate transactions. You are solely responsible for verifying the accuracy of any legal documents and handling the transfer of ownership of any property bought or sold through the platform.",
        "We highly recommend that you seek independent legal, financial, and real estate advice before entering into any transaction facilitated by the Swim Homes platform. The tools, checklists, and advice provided by Swim Homes are general in nature and do not substitute for professional services."
      ]
    },
    {
      title: "5. AI Tools and Features",
      content: [
        "Swim Homes provides various AI-powered tools and features, including \"the Captain\" AI assistant, to help users navigate the real estate process. These AI features are provided for informational and convenience purposes only and have the following limitations:"
      ],
      subsections: [
        {
          title: "a. Limited Scope of AI Tools",
          content: [
            "Our AI tools, including \"the Captain,\" are designed to provide general information about our services and basic guidance related to FSBO real estate transactions. The AI does not provide legal, financial, or real estate professional advice, and cannot replace the services of licensed professionals in these fields."
          ]
        },
        {
          title: "b. Prohibited Uses of AI",
          content: [
            "Users are strictly prohibited from using \"the Captain\" or any other AI tools provided by Swim Homes for any of the following purposes:",
            "Attempting to manipulate, deceive, or override the AI's built-in limitations",
            "Using the AI to generate, promote, or distribute harmful, offensive, or illegal content",
            "Engaging in automated or programmatic interactions with the AI",
            "Reverse engineering, decompiling, or otherwise attempting to extract the underlying model, code, or training data of the AI",
            "Using the AI to collect data about other users or for competitive research purposes",
            "Any other use that violates these Terms, applicable laws, or the rights of others"
          ]
        },
        {
          title: "c. Information Accuracy and \"Hallucinations\"",
          content: [
            "AI technology may occasionally produce incorrect, incomplete, or misleading information through a phenomenon known as \"hallucinations.\" While we strive to minimize such occurrences, they remain an inherent risk of current AI technology. Swim Homes is not responsible for any decisions made based on potentially inaccurate information provided by our AI tools."
          ]
        },
        {
          title: "d. External AI Limitations",
          content: [
            "\"The Captain\" and other AI features may incorporate or rely on third-party AI systems, models, or services. Swim Homes does not own, control, or have full visibility into these external AI components and their underlying algorithms. Accordingly, Swim Homes disclaims all liability for the functioning, responses, or availability of these third-party AI components. The limitations, disclaimers, and liability provisions in these Terms apply with equal force to any issues arising from these external AI components."
          ]
        },
        {
          title: "e. User Verification Responsibility",
          content: [
            "You are solely responsible for independently verifying any information provided by our AI tools before taking any action. This includes consulting official documentation, verifying information through authoritative sources, and seeking professional advice when appropriate."
          ]
        },
        {
          title: "f. Paramountcy of Written Policies",
          content: [
            "In the event of any contradiction or inconsistency between information provided by our AI tools and our official written policies (including these Terms, Privacy Policy, and other official documentation), the official written policies govern exclusively."
          ]
        },
        {
          title: "g. AI Data Collection and Usage",
          content: [
            "Your interactions with our AI tools are logged, stored, and may be used to improve our services. These interactions are subject to our Privacy Policy."
          ]
        }
      ]
    },
    {
      title: "6. User Content",
      content: [
        "Our Service allows you to post, link, store, share and otherwise make available certain information, text, graphics, videos, or other material ('Content').",
        "You are responsible for the Content that you post on or through the Service, including its legality, reliability, and appropriateness.",
        "By posting Content on or through the Service, you represent and warrant that: (i) the Content is yours (you own it) and/or you have the right to use it and the right to grant us the rights and license as provided in these Terms, and (ii) that the posting of your Content on or through the Service does not violate the privacy rights, publicity rights, copyrights, contract rights or any other rights of any person or entity."
      ]
    },
    {
      title: "7. User Responsibilities",
      content: [],
      subsections: [
        {
          title: "a. Accurate and Truthful Information",
          content: [
            "As a user of the Swim Homes platform, you agree to provide complete, accurate, and truthful information regarding any property you list for sale or any information provided in the course of negotiations with other users. You are solely responsible for ensuring that all details provided, including property descriptions, pricing, and any additional features or representations, are truthful and not misleading.",
            "Misrepresentation of any property or failure to disclose material facts can result in legal consequences and may lead to the termination of your account on Swim Homes."
          ]
        },
        {
          title: "b. Conducting Due Diligence",
          content: [
            "Swim Homes does not verify the accuracy or completeness of any information provided by users or third parties on the platform. It is your responsibility to conduct appropriate due diligence when buying or selling property. This includes, but is not limited to, verifying ownership, condition of the property, title searches, legal status, and any applicable municipal or zoning regulations. Swim Homes disclaims any responsibility for the outcome of transactions that occur on the platform."
          ]
        },
        {
          title: "c. Compliance with Laws",
          content: [
            "You agree to comply with all applicable local, provincial, and federal laws and regulations regarding the purchase, sale, or listing of real estate in Ontario. This includes adhering to the Real Estate and Business Brokers Act, 2002 (REBBA), Ontario regulations related to real estate transactions, and other related laws."
          ]
        }
      ]
    },
    {
      title: "8. Service Provided \"As Is, Where Is\"",
      content: [
        "Swim Homes provides the platform and services on an \"as is, where is\" basis. While we aim to offer the best tools and resources, we make no guarantees regarding the availability, accuracy, or completeness of the platform or the outcomes of using the platform. You agree that your use of the platform is at your own risk.",
        "Swim Homes disclaims any warranties, express or implied, including but not limited to any warranties of merchantability, fitness for a particular purpose, and non-infringement."
      ],
    },
    {
      title: "9. MLS Syndication and Third-Party Services",
      content: [
        "Swim Homes may work with licensed realtors to post your property listings on MLS or similar real estate services in Ontario. This syndication is for listing purposes only, and does not create any formal relationship between Swim Homes, the realtor, and you. No realtor-client or broker-client relationship is established between Swim Homes and its users. You should seek the services of a licensed realtor or legal professional if you require specific advice or representation in your real estate transaction."
      ],
    },
    {
      title: "10. Third-Party Service Providers and Independent Contractor Relationship",
      content: [
        "For certain services, including but not limited to photography, home staging, inspections, and other professional services that may be offered through the platform, Swim Homes may arrange for third-party service providers to deliver these services. These service providers are independent contractors and not employees, partners, or agents of Swim Homes. Swim Homes merely facilitates the connection between you and these third-party service providers.",
        "While Swim Homes makes reasonable efforts to vet and select qualified service providers, we do not control their day-to-day activities, professional judgments, or the manner in which they perform their services. Service providers maintain their own professional licenses, insurance, and business operations independent of Swim Homes."
      ]
    },
    {
      title: "11. Termination and Account Suspension",
      content: [
        "Swim Homes reserves the right to suspend or terminate your access to the platform at any time, with or without notice, if:",
        "You violate any provision of these Terms or any applicable law.",
        "You engage in fraudulent, abusive, or illegal activity.",
        "You misrepresent information related to a property or transaction.",
        "You fail to comply with our policies, including providing false or misleading information.",
        "You use the platform in a manner that may harm the company or other users.",
        "You misuse our AI tools or attempt to manipulate them in ways contrary to their intended use.",
        "Upon termination, all licenses and rights granted to you in these Terms will immediately cease."
      ]
    },
    {
      title: "12. Limitation of Liability",
      content: [
        "Swim Homes provides a neutral platform that facilitates communication between buyers and sellers. We are not a party to any agreements, contracts, or transactions made between buyers and sellers on the platform, and we do not guarantee the performance of any party to a transaction. Swim Homes makes no representations or warranties regarding the condition, legality, or accuracy of any listing or property advertised through our platform.",
        "To the fullest extent permitted by law, Swim Homes disclaims all liability for any damages or losses, whether direct, indirect, incidental, or consequential, arising out of your use of the platform, the listing of a property, the use of our AI tools, or the completion (or failure to complete) a transaction.",
        "**Limited Liability for Third-Party Services**  ",
        "Swim Homes' liability related to third-party services arranged through our platform is strictly limited to the cost paid for that specific service. In the event that you are dissatisfied with a third-party service provider's work, Swim Homes' sole obligation is to arrange a qualified replacement to re-perform the service or, at our discretion, to refund the amount paid for that specific service.",
        "Under no circumstances will Swim Homes be liable for indirect, special, incidental, exemplary, punitive, or consequential damages resulting from any third-party service, including but not limited to lost profits, missed selling opportunities, property damage, or time delays, regardless of whether such damages were foreseeable and whether Swim Homes was advised of the possibility of such damages."
      ]
    },
    {
      title: "13. Indemnification",
      content: [
        "You agree to indemnify, defend, and hold harmless Swim Homes, its affiliates, partners, officers, directors, agents, and employees from any claim, demand, liability, or damage, including reasonable attorneys' fees, arising from your use of the platform, your breach of these Terms, or your violation of any law or the rights of a third party.",
        "This indemnification obligation includes, but is not limited to, any claims related to property misrepresentation, breach of contract, or failure to comply with applicable real estate regulations. Swim Homes reserves the right to assume the exclusive defense of any matter subject to indemnification by you, at your expense."
      ]
    },
    {
      title: "14. Disclaimers",
      subsections: [
        {
          title: "a. General Advice Disclaimer",
          content: [
            "Swim Homes provides general real estate information, advice, and resources through the platform. However, this information is not tailored to your specific property or circumstances. We do not provide legal, financial, or professional real estate services. Users are encouraged to seek their own independent legal advice and consult with licensed real estate professionals for specific matters related to their transactions."
          ]
        },
        {
          title: "b. Third-Party Service Providers and Links",
          content: [
            "From time to time, Swim Homes may recommend or partner with third-party service providers, such as photographers, appraisers, or legal professionals, to enhance your experience on the platform. We do not warrant the quality or accuracy of services provided by these third parties, and any arrangements you make with third-party providers are independent of Swim Homes. You are solely responsible for any costs or liabilities arising from your use of third-party services.",
            "The platform may contain links to third-party websites, services, or applications that are not owned or controlled by Swim Homes. We are not responsible for the content, privacy policies, or practices of any third-party websites. By using the platform, you acknowledge and agree that Swim Homes will not be liable for any damages or losses caused by your use of any third-party services or links."
          ]
        }
      ]
    },
    {
      title: "15. Intellectual Property",
      content: [
        "Swim Homes owns all intellectual property rights to the platform, including all text, graphics, logos, and content made available through the website. This includes user-generated content and submissions, which upon submission, are licensed to Swim Homes for use in connection with the platform. By submitting content to the platform, you grant Swim Homes a non-exclusive, royalty-free, perpetual, and irrevocable right to use, reproduce, modify, and display the content for any purpose related to the operation and promotion of the platform."
      ]
    },
    {
      title: "16. Dispute Resolution",
      content: [
        "In the event of a dispute arising from these Terms or your use of the platform, you agree to negotiate in good faith with Swim Homes for at least 90 days before resorting to arbitration or legal action. If the dispute cannot be resolved through negotiation, it shall be referred to binding arbitration in Ontario, Canada. The arbitration will be conducted by a single arbitrator, and the arbitration will be conducted in writing unless the parties mutually agree otherwise.",
        "You agree that any dispute will be resolved exclusively by arbitration, and no class action or representative action may be brought against Swim Homes."
      ]
    },
    {
      title: "17. Governing Law and Jurisdiction",
      content: [
        "These Terms are governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein. Any disputes arising out of or related to these Terms or your use of the platform will be subject to the exclusive jurisdiction of the courts located in Ontario, Canada."
      ]
    },
    {
      title: "18. Changes to the Terms and Conditions",
      content: [
        "We may update these Terms from time to time. When we do, we will revise the 'Effective Date' at the top of this page. Any changes to the Terms will be effective immediately upon posting. Your continued use of the platform after any changes are posted constitutes your acceptance of the new Terms."
      ]
    },
    {
      title: "19. Force Majeure",
      content: [
        "Swim Homes shall not be liable for any failure to perform its obligations under this agreement where such failure is as a result of Acts of God (including fire, flood, earthquake, storm, hurricane or other natural disaster), war, invasion, act of foreign enemies, hostilities (whether war is declared or not), civil war, rebellion, revolution, insurrection, military or usurped power or confiscation, terrorist activities, nationalization, government sanction, blockage, embargo, labor dispute, strike, lockout or interruption or failure of electricity or internet service, or any other event beyond the reasonable control of Swim Homes."
      ]
    },
    {
      title: "20. Severability",
      content: [
        "If any provision of these Terms is found to be unenforceable or invalid under any applicable law, such unenforceability or invalidity shall not render these Terms unenforceable or invalid as a whole, and such provisions shall be deleted without affecting the remaining provisions herein."
      ]
    },
    {
      title: "21. Entire Agreement",
      content: [
        "These Terms, together with the Privacy Policy and AI Disclaimer, constitute the entire agreement between you and Swim Homes regarding the use of the platform and services, and supersede all prior and contemporaneous written or oral agreements, communications, and other understandings relating to the subject matter of these Terms."
      ]
    },
    {
      title: "22. Non-Waiver",
      content: [
        "The failure of Swim Homes to exercise or enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. No waiver by Swim Homes of any term or condition set forth in these Terms shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition."
      ]
    },
    {
      title: "23. Contact Information",
      content: [
        "If you have any questions or concerns regarding these Terms and Conditions, please contact us at: support@swimhomes.ca"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="grow py-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <FileText className="h-7 w-7 text-primary" />
            <h1 className="text-3xl font-bold">Terms of Service</h1>
          </div>

          <div className="mb-8">
            <p className="text-muted-foreground mb-2">Last updated: March 03, 2025</p>

            <div className="mt-6 prose prose-lg max-w-none">
              <p>
                {sections[0].content && sections[0].content[0]}
              </p>
              <p>
                {sections[0].content && sections[0].content[1]}
              </p>
            </div>
          </div>

          <div className="space-y-10 mt-12">
            {sections.filter(section => section.title).map((section, index) => (
              <div key={index} className="prose max-w-none">
                <LegalSection section={section} />
              </div>
            ))}
          </div>

          <div className="mt-16 border-t pt-6 mb-12">
            <p className="text-muted-foreground">
              If you have any questions about our Terms of Service, please contact us at{' '}
              <a href="mailto:support@swimhomes.ca" className="text-primary hover:underline">
                support@swimhomes.ca
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}